import { html, TemplateResult } from 'lit';
import type { Article } from '../article-cards.interface';
import { buildImageElement, buildHeading } from '../templates/article-cards.shared';
import type { CardRenderer, RendererConfig } from './article-cards.renderer';

/**
 * Renderer for the 'featured' display format.
 *
 * Always renders: image, description, Read More button.
 * Tags block is conditional on article.tags being non-empty.
 */
export class FeaturedRenderer implements CardRenderer {
  constructor(private readonly config: RendererConfig) {}

  renderCard(article: Article, index: number): TemplateResult {
    const { listId, headingLevel, imageSize, imagePosition, onCardClick, onCtaClick } = this.config;
    const { path, title, date, desc, tags, imageUrl, imageAlt } = article;
    const fullPath = `${path}.html`;
    const titleId = `${listId}-card-${index}`;

    return html`
      <div class="col">
        <article
          class="card h-100 overflow-hidden rounded flex-lg-row align-items-center text-left shadow linkedCard"
          tabindex="-1"
          @click="${onCardClick(article)}"
        >
          <div class="col bg-light d-flex align-self-stretch align-items-center p-0 overflow-hidden ratio ratio-16x9">
            ${buildImageElement(imageUrl, imageAlt, imageSize, imagePosition)}
          </div>
          <div class="col p-0 align-self-stretch">
            <div class="card-body d-flex flex-column h-100 p-4">
              ${buildHeading(headingLevel, 'fs-5 fw-bold', title, titleId)}
              <time datetime="${date}" class="d-inline-block mb-2">
                <span>${date}</span>
              </time>
              ${desc ? html`<p class="description mb-2">${desc}</p>` : ''}
              <div class="mb-4">
                <a
                  href="${fullPath}"
                  class="btn btn-primary"
                  aria-label="Read more about ${title}"
                  @click="${onCtaClick(article)}"
                >
                  Read More
                </a>
              </div>
              ${tags
                ? html`
                    <hr aria-hidden="true" role="presentation" />
                    <p class="tags fs-6 m-0">Tags: <strong>${tags}</strong></p>
                  `
                : ''}
            </div>
          </div>
        </article>
      </div>
    `;
  }

  renderSkeleton(): TemplateResult {
    return html`
      <div class="col article-skeleton">
        <div class="card h-100 overflow-hidden rounded flex-lg-row align-items-center shadow">
          <div class="col bg-light d-flex align-self-stretch align-items-center p-0 overflow-hidden ratio ratio-16x9">
            <span class="placeholder w-100 h-100"></span>
          </div>
          <div class="col p-0 align-self-stretch">
            <div class="card-body d-flex flex-column h-100 p-4 placeholder-glow">
              <span class="placeholder col-9 placeholder-lg mb-3"></span>
              <span class="placeholder col-4 placeholder-sm mb-3"></span>
              <span class="placeholder col-12 mb-2"></span>
              <span class="placeholder col-11 mb-2"></span>
              <span class="placeholder col-10 mb-4"></span>
              <span class="placeholder col-5 rounded-pill"></span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Featured always shows exactly one skeleton regardless of perRow/numRows.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getSkeletonCount(_perRow: number, _numRows: number): number {
    return 1;
  }
}
